import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, FileDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { sharedExportarPdf } from "../../utils/shared-functions";

export default function ChartEstadoPagosModal({ isOpen, data, total, hasNumeric, onClose }) {
    const { t } = useTranslation();
    const dialogRef = useRef(null);
    const contentRef = useRef(null);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handleKeyDown = (e) => {
            if (e.key === "Escape") onClose();
        };

        const { body } = document;
        const previousOverflow = body.style.overflow;
        body.style.overflow = "hidden";
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            body.style.overflow = previousOverflow;
        };
    }, [isOpen, onClose]);

    const placas = useMemo(() => {
        if (!Array.isArray(data)) return [];

        const map = new Map();
        for (const item of data) {
            const placa = item?.labelPlaca || item?.valuePlaca || "—";
            if (!map.has(placa)) {
                map.set(placa, {
                    placa,
                    total: 0,
                    hasNumeric: false,
                    rows: [],
                });
            }
            const grupo = map.get(placa);
            const cliente = item?.cliente ?? "";
            const numeroDocumento = item?.numeroDocumento ?? "";
            const eps = Array.isArray(item?.estadoPago) ? item.estadoPago : [];

            if (eps.length === 0) {
                grupo.rows.push({
                    cliente,
                    numeroDocumento,
                    estadoPago: "—",
                    metodos: [],
                    subtotal: 0,
                    hasSubtotal: false,
                });
                continue;
            }

            for (const ep of eps) {
                const estadoDesc = (ep?.descripcion ?? "").trim() || "—";
                const detalles = Array.isArray(ep?.detalle) ? ep.detalle : [];

                const metodos = [];
                let subtotal = 0;
                let hasSubtotal = false;

                for (const det of detalles) {
                    const metodoRaw = (det?.descripcion ?? "").trim();
                    const metodo = metodoRaw || estadoDesc;
                    const valueStr = String(det?.value ?? "").trim();

                    let monto = 0;
                    let isNumeric = false;
                    if (valueStr !== "") {
                        const parsed = Number(valueStr);
                        if (Number.isFinite(parsed)) {
                            monto = parsed;
                            isNumeric = true;
                        }
                    }

                    metodos.push({ metodo, monto, isNumeric });
                    subtotal += monto;
                    if (isNumeric) hasSubtotal = true;
                }

                grupo.rows.push({
                    cliente,
                    numeroDocumento,
                    estadoPago: estadoDesc,
                    metodos,
                    subtotal,
                    hasSubtotal,
                });

                grupo.total += subtotal;
                if (hasSubtotal) grupo.hasNumeric = true;
            }
        }

        return Array.from(map.values()).sort((a, b) => b.total - a.total);
    }, [data]);

    if (!isOpen) return null;
    if (typeof document === "undefined") return null;

    const handleOverlayMouseDown = (e) => {
        if (dialogRef.current && !dialogRef.current.contains(e.target)) onClose();
    };

    const formatAmount = (n, isNumeric = true) => {
        if (!isNumeric) return "—";
        const num = Number(n) || 0;
        return num.toLocaleString("es-BO", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const handleExport = () => {
        if (isExporting) return;
        sharedExportarPdf(contentRef, "estado-pagos", {
            onStart: () => setIsExporting(true),
            onFinish: () => setIsExporting(false),
            onError: () => setIsExporting(false),
        });
    };

    const labelTotal = t("label_total") || "Total";
    const title = `${labelTotal} — ${formatAmount(total, hasNumeric)}`;

    const modal = (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4"
            onMouseDown={handleOverlayMouseDown}
            role="presentation"
        >
            <div
                ref={dialogRef}
                className="bg-white rounded-xl shadow-xl border border-neutral-200 w-full max-w-6xl max-h-[90vh] flex flex-col"
                role="dialog"
                aria-modal="true"
            >
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-neutral-200">
                    <h3 className="text-lg font-semibold text-neutral-800 leading-snug min-w-0 line-clamp-2">
                        {title}
                    </h3>
                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={handleExport}
                            disabled={isExporting || placas.length === 0}
                            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FileDown className="h-4 w-4" />
                            {isExporting ? `${t("label_exportar_pdf")}…` : t("label_exportar_pdf")}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
                            aria-label={t("label_cerrar")}
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div
                    ref={contentRef}
                    className="flex-1 min-h-0 overflow-y-auto p-4 bg-white text-black space-y-5"
                    style={{ fontFamily: "sans-serif" }}
                >
                    <div data-pdf-block className="space-y-5">
                        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 flex items-center justify-between">
                            <span className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
                                {labelTotal}
                            </span>
                            <span
                                className={`text-base font-bold tabular-nums ${hasNumeric ? "text-neutral-900" : "text-neutral-400 italic"
                                    }`}
                            >
                                {formatAmount(total, hasNumeric)}
                            </span>
                        </div>

                    {placas.length === 0 ? (
                        <p className="text-sm text-neutral-500">
                            {t("label_sin_datos")}
                        </p>
                    ) : (
                        placas.map((p) => (
                            <div key={p.placa}>
                                <h2 className="text-base font-semibold text-neutral-900 mb-2 flex items-baseline gap-2">
                                    <span>
                                        {t("label_placa") || "Placa"} {p.placa}
                                    </span>
                                    <span
                                        className={`text-sm font-normal ${p.hasNumeric ? "text-neutral-500" : "text-neutral-400 italic"
                                            }`}
                                    >
                                        ({formatAmount(p.total, p.hasNumeric)})
                                    </span>
                                </h2>
                                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                    <table className="w-full max-w-full text-sm border-collapse table-fixed">
                                        <colgroup>
                                            <col style={{ width: "4%" }} />
                                            <col style={{ width: "24%" }} />
                                            <col style={{ width: "10%" }} />
                                            <col style={{ width: "16%" }} />
                                            <col style={{ width: "32%" }} />
                                            <col style={{ width: "14%" }} />
                                        </colgroup>
                                        <thead>
                                            <tr className="bg-gray-50 text-xs font-semibold text-gray-600 border-b border-gray-200">
                                                <th scope="col" className="text-left py-2.5 px-3 align-top leading-normal">#</th>
                                                <th scope="col" className="text-left py-2.5 px-3 align-top leading-normal">{t("label_cliente") || "Cliente"}</th>
                                                <th scope="col" className="text-left py-2.5 px-3 align-top leading-normal">{t("label_documento") || "N° Doc"}</th>
                                                <th scope="col" className="text-left py-2.5 px-3 align-top leading-normal">{t("label_estado_pago") || "Estado pago"}</th>
                                                <th scope="col" className="text-left py-2.5 px-3 align-top leading-normal">{t("label_metodo") || "Métodos"}</th>
                                                <th scope="col" className="text-right py-2.5 px-3 align-top leading-normal">{t("label_subtotal") || "Subtotal"}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {p.rows.map((r, idx) => (
                                                <tr
                                                    key={`${r.numeroDocumento}-${idx}`}
                                                    className="border-b border-gray-100 align-top"
                                                >
                                                    <td className="py-2 px-3 text-gray-700 tabular-nums">{idx + 1}</td>
                                                    <td className="py-2 px-3 text-gray-900 break-words">{r.cliente}</td>
                                                    <td className="py-2 px-3 text-gray-900 break-words">{r.numeroDocumento}</td>
                                                    <td className="py-2 px-3 text-gray-900 break-words">{r.estadoPago}</td>
                                                    <td className="py-2 px-3 text-gray-900">
                                                        {r.metodos.length === 0 ? (
                                                            <span className="text-gray-400 italic">—</span>
                                                        ) : (
                                                            <ul className="space-y-0.5">
                                                                {r.metodos.map((m, mi) => (
                                                                    <li
                                                                        key={`${m.metodo}-${mi}`}
                                                                        className="flex items-baseline justify-between gap-2"
                                                                    >
                                                                        <span className="capitalize break-words min-w-0">
                                                                            {m.metodo}
                                                                        </span>
                                                                        <span
                                                                            className={`tabular-nums shrink-0 ${m.isNumeric
                                                                                    ? "text-gray-900 font-medium"
                                                                                    : "text-gray-400 italic"
                                                                                }`}
                                                                        >
                                                                            {formatAmount(m.monto, m.isNumeric)}
                                                                        </span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </td>
                                                    <td
                                                        className={`py-2 px-3 text-right tabular-nums font-medium ${r.hasSubtotal ? "text-gray-900" : "text-gray-400 italic"
                                                            }`}
                                                    >
                                                        {formatAmount(r.subtotal, r.hasSubtotal)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="bg-gray-50 border-t border-gray-200 font-semibold text-gray-900">
                                                <td colSpan={5} className="py-2 px-3 text-left">
                                                    {labelTotal} {p.placa}
                                                </td>
                                                <td
                                                    className={`py-2 px-3 text-right tabular-nums ${p.hasNumeric ? "" : "text-gray-400 italic"
                                                        }`}
                                                >
                                                    {formatAmount(p.total, p.hasNumeric)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        ))
                    )}
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}
